import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { heroHeadlines } from '../data/heroHeadlines'
import { duration, ease } from '../lib/motion'

const INTERVAL_MS = 4800

// Cycles the hero H1 through a handful of audience-specific lines. Only the
// text animates — its position in the layout, the surrounding subhead and
// buttons, and the H1's own font size/weight never change.
//
// Auto-rotation pauses on hover/focus (so it never fights someone trying to
// read mid-transition) and is skipped entirely for prefers-reduced-motion,
// where the first (general) headline just stays put.
export default function RotatingHeadline() {
  const [index, setIndex] = useState(0)
  const [paused, setPaused] = useState(false)
  const reducedMotion = useRef(false)

  useEffect(() => {
    reducedMotion.current = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  }, [])

  useEffect(() => {
    if (reducedMotion.current || paused) return
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % heroHeadlines.length)
    }, INTERVAL_MS)
    return () => clearInterval(id)
  }, [paused])

  const current = heroHeadlines[index]

  return (
    <h1
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      className="font-display font-semibold leading-[0.98] tracking-tight text-[44px] sm:text-[64px] lg:text-[80px]"
      style={{ color: 'var(--text-primary)' }}
    >
      <AnimatePresence mode="wait">
        <motion.span
          key={index}
          initial={reducedMotion.current ? false : { opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          exit={reducedMotion.current ? undefined : { opacity: 0, y: -18 }}
          transition={{ duration: duration.base, ease: ease.out }}
          className="block"
        >
          {current.lead}{' '}
          <span className="italic font-normal" style={{ color: 'var(--accent)' }}>
            {current.accent}
          </span>
        </motion.span>
      </AnimatePresence>
    </h1>
  )
}
